import axios from 'axios';
import { getToken, clearAuth } from './authToken';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      clearAuth();
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  login: (username: string, password: string, remember = false) =>
    api.post('/auth/login', { username, password, remember }),
};

export const protocolAPI = {
  list: () => api.get('/protocols'),
  get: (id: number) => api.get(`/protocols/${id}`),
  create: (data: any) => api.post('/protocols', data),
  update: (id: number, data: any) => api.put(`/protocols/${id}`, data),
  delete: (id: number) => api.delete(`/protocols/${id}`),
  bulkDelete: (ids: number[]) => api.post('/protocols/bulk-delete', { ids }),
  downloadHwpx: (id: number) =>
    api.get(`/protocols/${id}/hwpx`, { responseType: 'blob' }),
  uploadHwpx: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/protocols/upload-hwpx', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  previewHwpx: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/protocols/preview-hwpx', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const scheduleAPI = {
  // test_year 는 콤마 구분 다중 선택 가능 (예: '25,26') — 2자리·4자리 표기 모두 허용
  /* page 를 주면 서버 페이징 응답({items,total,page,page_size}), 없으면 기존처럼 배열 전체 */
  list: (params?: { search?: string; test_year?: string; category_id?: number; company_id?: number;
                    location_id?: number; request_type?: string; status?: string; period?: string;
                    page?: number; page_size?: number; sort_by?: string; sort_dir?: string }) =>
    api.get('/schedules', { params }),
  statusSummary: (params?: { search?: string; test_year?: string; category_id?: number; company_id?: number;
                             location_id?: number; request_type?: string; period?: string }) =>
    api.get('/schedules/status-summary', { params }),
  get: (id: number) => api.get(`/schedules/${id}`),
  create: (data: any) => api.post('/schedules', data),
  update: (id: number, data: any) => api.put(`/schedules/${id}`, data),
  delete: (id: number) => api.delete(`/schedules/${id}`),
  bulk: (data: any) => api.post('/schedules/bulk', data),
  bulkDates: (data: { ids: number[]; report_date?: string; tims_status?: string }) =>
    api.post('/schedules/bulk-dates', data),
  stats: () => api.get('/schedules/stats'),
  filters: () => api.get('/schedules/filters'),
  uploadExcel: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/schedules/upload-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  exportExcel: (test_year?: string) =>
    api.get('/schedules/export/excel', { params: { test_year }, responseType: 'blob' }),
  downloadTemplate: () =>
    api.get('/schedules/template', { responseType: 'blob' }),
};

// 용어사전 API
export const glossaryAPI = {
  list: (params?: { search?: string; category?: string; needs_review?: string }) =>
    api.get('/glossary', { params }),
  get: (id: number) => api.get(`/glossary/${id}`),
  create: (data: any) => api.post('/glossary', data),
  update: (id: number, data: any) => api.put(`/glossary/${id}`, data),
  delete: (id: number) => api.delete(`/glossary/${id}`),
  bulkUpsert: (items: any[]) => api.post('/glossary/bulk-upsert', { items }),
};

// 시험물질관리대장 API
export const testMaterialAPI = {
  list: (params?: {
    search?: string; company_name?: string; manufacturer?: string;
    crop_disposal?: string; disposed?: string; storage_status?: string;
    // 시험설계서 목록과 같은 축 — 시험번호·항목명에서 파생한 값이라 서버가 판정한다
    test_year?: string; crop?: string; target_disease?: string; pesticide_name?: string;
    page?: number; page_size?: number;
  }) => api.get('/test-materials', { params }),
  filters: () => api.get('/test-materials/filters'),
  // 검색 결과 요약 카드용 집계 (보관·기한 상태가 카드 축이라 storage_status 는 파라미터에서 제외)
  summary: (params?: {
    search?: string; company_name?: string; manufacturer?: string; crop_disposal?: string;
    test_year?: string; crop?: string; target_disease?: string; pesticide_name?: string;
  }) => api.get('/test-materials/summary', { params }),
  get: (id: number) => api.get(`/test-materials/${id}`),
  create: (data: any) => api.post('/test-materials', data),
  update: (id: number, data: any) => api.put(`/test-materials/${id}`, data),
  delete: (id: number) => api.delete(`/test-materials/${id}`),
  bulkDates: (data: { ids: number[]; received_date?: string; disposal_date?: string }) =>
    api.post('/test-materials/bulk-dates', data),
  uploadExcel: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/test-materials/upload-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  exportExcel: () => api.get('/test-materials/export/excel', { responseType: 'blob' }),
};

// 마스터 API
export const masterAPI = {
  // 의뢰사
  companies: {
    list: () => api.get('/master/companies'),
    create: (data: any) => api.post('/master/companies', data),
    update: (id: number, data: any) => api.put(`/master/companies/${id}`, data),
    delete: (id: number) => api.delete(`/master/companies/${id}`),
  },
  // 시험장소
  locations: {
    list: () => api.get('/master/locations'),
    create: (data: any) => api.post('/master/locations', data),
    update: (id: number, data: any) => api.put(`/master/locations/${id}`, data),
    delete: (id: number) => api.delete(`/master/locations/${id}`),
  },
  // 구분
  categories: {
    list: () => api.get('/master/categories'),
    create: (data: any) => api.post('/master/categories', data),
    update: (id: number, data: any) => api.put(`/master/categories/${id}`, data),
    delete: (id: number) => api.delete(`/master/categories/${id}`),
  },
  // 포장주
  fieldOwners: {
    list: () => api.get('/master/field-owners'),
    create: (data: any) => api.post('/master/field-owners', data),
    update: (id: number, data: any) => api.put(`/master/field-owners/${id}`, data),
    delete: (id: number) => api.delete(`/master/field-owners/${id}`),
  },
  // 차량
  vehicles: {
    list: () => api.get('/master/vehicles'),
    create: (data: any) => api.post('/master/vehicles', data),
    update: (id: number, data: any) => api.put(`/master/vehicles/${id}`, data),
    delete: (id: number) => api.delete(`/master/vehicles/${id}`),
  },
  // 연구원
  researchers: {
    list: () => api.get('/master/researchers'),
    create: (data: any) => api.post('/master/researchers', data),
    update: (id: number, data: any) => api.put(`/master/researchers/${id}`, data),
    delete: (id: number) => api.delete(`/master/researchers/${id}`),
  },
  statusCodes: {
    list: () => api.get('/master/status-codes'),
    create: (data: any) => api.post('/master/status-codes', data),
    update: (id: number, data: any) => api.put(`/master/status-codes/${id}`, data),
    delete: (id: number) => api.delete(`/master/status-codes/${id}`),
  },
};

export const dashboardAPI = {
  stats: () => api.get('/dashboard'),
  alerts: () => api.get('/dashboard/alerts'),
  /** 파트너 문의/요청 중 답변 대기 건 (메인화면 알림) */
  inquiryAlerts: () => api.get('/dashboard/inquiry-alerts'),
  upcoming: (year: number, month: number) => api.get('/dashboard/upcoming', { params: { year, month } }),
  photoSchedules: (category: string, photo: 'yes' | 'no' | 'none' | 'all') =>
    api.get('/dashboard/photo-schedules', { params: { category, photo } }),
  /** 진행상태별 현황 클릭 → 세부 목록. years 는 2자리 연도 배열(빈 배열이면 전체 연도) */
  statusSchedules: (status: string, years: string[] = []) =>
    api.get('/dashboard/status-schedules', { params: { status, year: years.join(',') } }),
};

// 권한 관리 API
export const authMeAPI = {
  me: () => api.get('/auth/me'),
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/auth/change-password', data),
};

export const permissionAPI = {
  list: () => api.get('/permissions'),
  matrix: () => api.get('/permissions/matrix'),
};

export const roleAPI = {
  list: () => api.get('/roles'),
  create: (data: { role_code: string; role_name: string; description?: string }) =>
    api.post('/roles', data),
  update: (id: number, data: { role_name?: string; description?: string; is_active?: boolean }) =>
    api.put(`/roles/${id}`, data),
  delete: (id: number) => api.delete(`/roles/${id}`),
  setPermissions: (id: number, data: { permission_action_ids: number[] }) =>
    api.put(`/roles/${id}/permissions`, data),
};

export const menuOrderAPI = {
  get: () => api.get('/permissions/menu-order'),
  save: (data: { groups: { group_name: string; group_sort_order: number; menus: { menu_code: string; sort_order: number; menu_name?: string }[] }[] }) =>
    api.put('/permissions/menu-order', data),
  reset: () => api.post('/permissions/menu-order/reset'),
};

export const userAPI = {
  list: () => api.get('/users'),
  create: (data: { username: string; password: string; full_name: string; role_ids: number[] }) =>
    api.post('/users', data),
  update: (id: number, data: { full_name?: string; password?: string; is_active?: boolean; role_ids?: number[] }) =>
    api.put(`/users/${id}`, data),
  delete: (id: number) => api.delete(`/users/${id}`),
  resetPassword: (id: number, data: { new_password: string }) =>
    api.post(`/users/${id}/reset-password`, data),
};

// 파트너 계정 관리 API
// 파트너 문의/요청 (lab 담당자 답변)
export const inquiryAPI = {
  list: (params?: { status?: string; inquiry_type?: string; company_id?: number; schedule_id?: number; keyword?: string; unread_only?: boolean }) =>
    api.get('/inquiries', { params }),
  detail: (id: number) => api.get(`/inquiries/${id}`),
  /** 시험일정총괄표 배지용 시험별 집계 */
  scheduleSummary: () => api.get('/inquiries/schedule-summary'),
  reply: (id: number, content: string) => api.post(`/inquiries/${id}/replies`, { content }),
  uploadFile: (inquiryId: number, file: File, replyId?: number) => {
    const fd = new FormData();
    fd.append('file', file);
    if (replyId) fd.append('reply_id', String(replyId));
    return api.post(`/inquiries/${inquiryId}/attachments`, fd, { timeout: 120000 });
  },
  downloadFile: (attachmentId: number) =>
    api.get(`/inquiry-attachments/${attachmentId}/download`, { responseType: 'blob', timeout: 120000 }),
  deleteFile: (attachmentId: number) => api.delete(`/inquiry-attachments/${attachmentId}`),
  updateStatus: (id: number, status: string) => api.put(`/inquiries/${id}/status`, { status }),
  delete: (id: number) => api.delete(`/inquiries/${id}`),
};

export const partnerAccountAPI = {
  list: () => api.get('/partner-accounts'),
  create: (data: { username: string; password: string; full_name: string; company_id: number }) =>
    api.post('/partner-accounts', data),
  update: (id: number, data: { full_name?: string; password?: string; company_id?: number }) =>
    api.put(`/partner-accounts/${id}`, data),
  delete: (id: number) => api.delete(`/partner-accounts/${id}`),
  scheduleAccess: {
    list: (accountId: number) => api.get(`/partner-accounts/${accountId}/schedule-access`),
    add: (accountId: number, scheduleIds: number[], note?: string) =>
      api.post(`/partner-accounts/${accountId}/schedule-access`, { schedule_ids: scheduleIds, note }),
    remove: (accountId: number, scheduleId: number) =>
      api.delete(`/partner-accounts/${accountId}/schedule-access/${scheduleId}`),
  },
  visibleSchedules: (accountId: number) => api.get(`/partner-accounts/${accountId}/visible-schedules`),
};

// 활동이력 API
export const activityLogAPI = {
  list: (params?: {
    action?: string; target_type?: string; username?: string; keyword?: string;
    date_from?: string; date_to?: string; page?: number; page_size?: number;
  }) => api.get('/activity-logs', { params }),
  stats: (params?: {
    action?: string; target_type?: string; username?: string; keyword?: string;
    date_from?: string; date_to?: string;
  }) => api.get('/activity-logs/stats', { params }),
  logPageView: (path: string, name: string) =>
    api.post('/activity-logs/page-view', { path, name }).catch(() => {}),
};

// 서버 모니터링 API
export const monitoringAPI = {
  serverStatus: () => api.get('/monitoring/server-status'),
  ociCost: () => api.get('/monitoring/oci-cost'),
  ghcr: () => api.get('/monitoring/ghcr'),
  backupStatus: () => api.get('/monitoring/backup-status'),
  containerLogs: (params: { container?: string; lines?: number; search?: string; level?: string }) =>
    api.get('/monitoring/container-logs', { params }),
  systemLogs: (params: { file: string; lines?: number }) =>
    api.get('/monitoring/system-logs', { params }),
};

// ADB 데모 (OCI Autonomous Database CRUD 예제)
export interface DemoItem {
  id: number;
  name: string;
  qty: number;
  note?: string;
  created_at?: string;
}
export const adbDemoAPI = {
  health: () => api.get('/adb-demo/health'),
  list: () => api.get('/adb-demo/items'),
  create: (data: { name: string; qty: number; note?: string }) =>
    api.post('/adb-demo/items', data),
  update: (id: number, data: { name: string; qty: number; note?: string }) =>
    api.put(`/adb-demo/items/${id}`, data),
  remove: (id: number) => api.delete(`/adb-demo/items/${id}`),
};

// 챗봇 API
export const chatbotLogAPI = {
  list: (params?: { page?: number; page_size?: number; username?: string; route?: string; days?: number }) =>
    api.get('/chatbot/logs', { params }),
  stats: (params?: { days?: number }) => api.get('/chatbot/logs/stats', { params }),
};

export const chatbotAPI = {
  send: (message: string) => api.post('/chatbot', { message }),
  /** SSE 스트리밍 — onChunk: 토큰 단위, onReply: 전체 치환, onDone: 완료 */
  stream: async (
    message: string,
    handlers: { onChunk?: (t: string) => void; onReply?: (t: string) => void; onDone?: () => void; onError?: (e: unknown) => void }
  ): Promise<void> => {
    const base = process.env.REACT_APP_API_URL || '/api';
    const token = getToken() || '';
    try {
      const res = await fetch(`${base}/chatbot/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split(String.fromCharCode(10)+String.fromCharCode(10));
        buffer = events.pop() || '';
        for (const ev of events) {
          const lines = ev.split(String.fromCharCode(10));
          const evName = lines.find((l) => l.startsWith('event: '))?.slice(7) || '';
          const dataLine = lines.find((l) => l.startsWith('data: '))?.slice(6) || '';
          if (!dataLine) continue;
          try {
            const data = JSON.parse(dataLine);
            if (evName === 'chunk' && data.text) handlers.onChunk?.(data.text);
            else if (evName === 'reply' && data.text) handlers.onReply?.(data.text);
            else if (evName === 'done') handlers.onDone?.();
          } catch {}
        }
      }
      handlers.onDone?.();
    } catch (e) {
      handlers.onError?.(e);
    }
  },
};

// 글로벌 검색 API (OpenSearch)
export const searchAPI = {
  global: (q: string) => api.get('/search', { params: { q } }),
};

// OpenSearch 관리 API
export const opensearchAPI = {
  health: () => api.get('/opensearch/health'),
  popularSearches: (days?: number) => api.get('/opensearch/popular-searches', { params: { days } }),
  resync: () => api.post('/opensearch/resync'),
};

// 출장 관리 API
export const businessTripAPI = {
  list: (params?: { year?: number; month?: number; researcher?: string }) =>
    api.get('/business-trips', { params }),
  get: (id: number) => api.get(`/business-trips/${id}`),
  create: (data: any) => api.post('/business-trips', data),
  bulkCreate: (data: any[]) => api.post('/business-trips/bulk', data),
  update: (id: number, data: any) => api.put(`/business-trips/${id}`, data),
  delete: (id: number) => api.delete(`/business-trips/${id}`),
  importExcel: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/business-trips/import-excel', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const boardAPI = {
  list: (params?: { search?: string; category?: string; page?: number; page_size?: number }) =>
    api.get('/boards', { params }),
  get: (id: number) => api.get(`/boards/${id}`),
  create: (data: { title: string; content?: string; category?: string; is_pinned?: boolean }) =>
    api.post('/boards', data),
  update: (id: number, data: { title?: string; content?: string; category?: string; is_pinned?: boolean }) =>
    api.put(`/boards/${id}`, data),
  delete: (id: number) => api.delete(`/boards/${id}`),
  uploadAttachments: (boardId: number, files: File[], onProgress?: (e: any) => void) => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return api.post(`/boards/${boardId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  downloadAttachment: (attachmentId: number) =>
    api.get(`/board-attachments/${attachmentId}/download`, { responseType: 'blob' }),
  deleteAttachment: (attachmentId: number) =>
    api.delete(`/board-attachments/${attachmentId}`),
};

export const registryAPI = {
  catalog: () => api.get('/registry/catalog'),
  tags: (imageName: string) => api.get(`/registry/tags/${imageName}`),
  manifest: (imageName: string, tag: string) => api.get(`/registry/manifest/${imageName}/${tag}`),
  deleteManifest: (imageName: string, digest: string) => api.delete(`/registry/manifest/${imageName}/${digest}`),
  health: () => api.get('/registry/health'),
};

export const storageAPI = {
  health: () => api.get('/storage/health'),
  listObjects: (prefix?: string, delimiter?: string) =>
    api.get('/storage/objects', { params: { ...(prefix ? { prefix } : {}), ...(delimiter ? { delimiter } : {}) } }),
  deleteObject: (objectKey: string) => api.delete(`/storage/objects/${objectKey}`),
  uploadObject: (formData: FormData, prefix?: string) =>
    api.post('/storage/objects/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: prefix ? { prefix } : {},
    }),
  createFolder: (folderName: string, prefix?: string) =>
    api.post('/storage/folders', { folder_name: folderName, prefix }),
  getDownloadUrl: (objectKey: string) =>
    api.get(`/storage/objects/${objectKey}/download-url`),
};

export const qrAPI = {
  list: (params?: { target_type?: string; target_id?: number; page?: number; page_size?: number }) =>
    api.get('/qr-codes', { params }),
  generate: (data: { target_type: string; target_id: number; redirect_url: string; description?: string }) =>
    api.post('/qr-codes/generate', data),
  updateRedirect: (shortCode: string, data: { redirect_url?: string; is_active?: boolean; description?: string }) =>
    api.put(`/qr-codes/${shortCode}/redirect`, data),
  logs: (shortCode: string, params?: { page?: number; page_size?: number }) =>
    api.get(`/qr-codes/${shortCode}/logs`, { params }),
  stats: () => api.get('/qr-codes/stats/summary'),
  imageUrl: (shortCode: string) => `/api/qr/${shortCode}/image`,
};

export const ncpmsAPI = {
  // 병해충 도감 (실시간 프록시 — 외부 API라 타임아웃 여유)
  search: (keyword: string, page = 1, pageSize = 20) =>
    api.get('/ncpms/search', { params: { keyword, page, page_size: pageSize }, timeout: 45000 }),
  detail: (serviceCode: string, key: string) =>
    api.get('/ncpms/detail', { params: { service_code: serviceCode, key }, timeout: 45000 }),
  // 예찰정보 (로컬 캐시)
  surveys: (params?: { year?: string; crop?: string; kind?: string; page?: number; page_size?: number }) =>
    api.get('/ncpms/surveys', { params }),
  surveyFilters: () => api.get('/ncpms/surveys/filters'),
  surveyDetail: (insectKey: string) => api.get(`/ncpms/surveys/${insectKey}`, { timeout: 45000 }),
  surveyAnalysis: (params: { crop: string; year?: string; survey_kind?: string }) =>
    api.get('/ncpms/surveys/analysis', { params, timeout: 45000 }),
  sync: () => api.post('/ncpms/sync', null, { params: { detail_limit: 100 }, timeout: 300000 }),
  // 병해충 상담 Q&A (실시간 프록시)
  consults: (page = 1, pageSize = 20) =>
    api.get('/ncpms/consults', { params: { page, page_size: pageSize }, timeout: 45000 }),
  consultDetail: (dgnssReqNo: string) => api.get(`/ncpms/consults/${dgnssReqNo}`, { timeout: 45000 }),
};

export const inspectionAPI = {
  list: (year?: string) => api.get('/inspections', { params: year ? { year } : {} }),
  detail: (id: number) => api.get(`/inspections/${id}`),
  run: () => api.post('/inspections/run', null, { timeout: 120000 }),
  remove: (id: number) => api.delete(`/inspections/${id}`),
  pdf: (id: number) => api.get(`/inspections/${id}/pdf`, { responseType: 'blob', timeout: 60000 }),
  getSettings: () => api.get('/inspections/settings'),
  updateSettings: (data: { schedule_type: string; anchor_day: number }) =>
    api.put('/inspections/settings', data),
};

/** 알림센터 — 운영 시스템 알림 (Discord 발송분 미러링) */
export const systemAlertAPI = {
  /** 헤더 알림 벨 — 미읽음 건수 + 최근 알림 */
  summary: (limit = 8) => api.get('/system-alerts/summary', { params: { limit } }),
  list: (params: {
    category?: string; level?: string; unread_only?: boolean;
    search?: string; page?: number; page_size?: number;
  }) => api.get('/system-alerts', { params }),
  read: (id: number) => api.post(`/system-alerts/${id}/read`),
  readAll: () => api.post('/system-alerts/read-all'),
  remove: (id: number) => api.delete(`/system-alerts/${id}`),
};

/** 화면 테스트(Playwright) — 실행 큐 + 결과 조회 */
export const uiTestAPI = {
  /** 상단 요약 — 최신 실행·실패 케이스·미커버 화면·추세 */
  summary: () => api.get('/ui-tests/summary'),
  /** 화면별 최근 결과 (커버리지 표) */
  coverage: () => api.get('/ui-tests/coverage'),
  list: (params: { status?: string; trigger_type?: string; page?: number; page_size?: number }) =>
    api.get('/ui-tests', { params }),
  detail: (id: number) => api.get(`/ui-tests/${id}`),
  remove: (id: number) => api.delete(`/ui-tests/${id}`),
  /** 지금 실행 — 대기 작업을 만들고 호스트 워처가 1분 안에 가져간다 */
  request: (targets?: string) => api.post('/ui-tests/request', targets ? { targets } : {}),
  getSettings: () => api.get('/ui-tests/settings'),
  updateSettings: (data: {
    enabled?: boolean; targets?: string; keep_days?: number; notify_level?: string;
  }) => api.put('/ui-tests/settings', data),
  /* 캡처는 권한 검사가 붙은 엔드포인트라 <img src>로 못 받는다 (Authorization 헤더가 안 실림).
     blob 으로 받아 objectURL 로 그린다. */
  /** 화면 갤러리 캡처 (최근 실행분, 고정 키) */
  screenShot: (site: string, code: string) =>
    api.get(`/ui-tests/screens/${site}/${code}/shot`, { responseType: 'blob' }),
  /** 실패 케이스 캡처 */
  caseShot: (caseId: number) =>
    api.get(`/ui-tests/cases/${caseId}/shot`, { responseType: 'blob' }),
  /** 회차별 결과보고서 PDF — 저장하지 않고 누를 때 생성된다 */
  reportPdf: (id: number) => api.get(`/ui-tests/${id}/report/pdf`, { responseType: 'blob' }),
  /** 결과보고서 확인(승인) — 실행 상태는 바꾸지 않는다 */
  approve: (id: number, approver?: string) =>
    api.post(`/ui-tests/${id}/approve`, approver ? { approver } : {}),
};

/** 화면테스트 문서 산출물 — 계획서 · 케이스명세 · 테스트데이터 · 결함대장
 *  권한은 화면테스트와 같은 `ui_tests` 메뉴를 쓴다. */
export const testDocAPI = {
  // ── 계획서 ──
  plans: (status?: string) => api.get('/test-plans', { params: status ? { status } : {} }),
  plan: (id: number) => api.get(`/test-plans/${id}`),
  createPlan: (data: any) => api.post('/test-plans', data),
  updatePlan: (id: number, data: any) => api.put(`/test-plans/${id}`, data),
  removePlan: (id: number) => api.delete(`/test-plans/${id}`),
  /** 승인 = 그 시점 레지스트리를 baseline 으로 동결 */
  approvePlan: (id: number, approver?: string) =>
    api.post(`/test-plans/${id}/approve`, approver ? { approver } : {}),
  archivePlan: (id: number) => api.post(`/test-plans/${id}/archive`),
  /** 레지스트리 변경분 재시드 — 손댄 행(is_custom)은 보존된다 */
  reseedPlan: (id: number) => api.post(`/test-plans/${id}/reseed`),
  planPdf: (id: number) => api.get(`/test-plans/${id}/pdf`, { responseType: 'blob' }),

  // ── 케이스 명세 ──
  cases: (planId: number, params?: {
    site?: string; screen_code?: string; priority?: string; search?: string;
  }) => api.get(`/test-plans/${planId}/cases`, { params }),
  updateCase: (caseId: number, data: any) => api.put(`/test-plans/cases/${caseId}`, data),
  casesPdf: (planId: number) => api.get(`/test-plans/${planId}/cases/pdf`, { responseType: 'blob' }),

  // ── 테스트 데이터 ──
  datasets: (planId?: number) =>
    api.get('/test-datasets', { params: planId ? { plan_id: planId } : {} }),
  createDataset: (data: any) => api.post('/test-datasets', data),
  updateDataset: (id: number, data: any) => api.put(`/test-datasets/${id}`, data),
  removeDataset: (id: number) => api.delete(`/test-datasets/${id}`),

  // ── 결함대장 ──
  defectSummary: () => api.get('/test-defects/summary'),
  defects: (params: {
    status?: string; severity?: string; site?: string; open_only?: boolean;
    search?: string; page?: number; page_size?: number;
  }) => api.get('/test-defects', { params }),
  createDefect: (data: any) => api.post('/test-defects', data),
  defectFromCase: (caseId: number, data?: any) =>
    api.post(`/test-defects/from-case/${caseId}`, data || {}),
  updateDefect: (id: number, data: any) => api.put(`/test-defects/${id}`, data),
  removeDefect: (id: number) => api.delete(`/test-defects/${id}`),
};

export default api;

/** 하우스 온습도로거 (Dragino S31-CB) — 장치 대장 · 측정 데이터 · 업링크/거절 로그 */
export const thloggerAPI = {
  summary: () => api.get('/thlogger/summary'),
  /** 현황판 — 활성 장치 전체의 최근 N시간 1시간 평균 + 24시간 최저·최고 */
  overview: (hours = 48) => api.get('/thlogger/overview', { params: { hours } }),
  /** 장치별 최근 N일 업링크 시각 — 장치 대장 '최근 수신' 띠 */
  uplinkHistory: (days = 14) => api.get('/thlogger/uplink-history', { params: { days } }),
  config: () => api.get('/thlogger/config'),
  devices: (includeInactive = false) => api.get('/thlogger/devices', { params: { include_inactive: includeInactive } }),
  device: (id: number) => api.get(`/thlogger/devices/${id}`),
  createDevice: (data: any) => api.post('/thlogger/devices', data),
  updateDevice: (id: number, data: any) => api.put(`/thlogger/devices/${id}`, data),
  deleteDevice: (id: number) => api.delete(`/thlogger/devices/${id}`),
  assignments: (deviceId: number) => api.get(`/thlogger/devices/${deviceId}/assignments`),
  createAssignment: (deviceId: number, data: any) => api.post(`/thlogger/devices/${deviceId}/assignments`, data),
  closeAssignment: (id: number, validTo?: string) => api.put(`/thlogger/assignments/${id}/close`, null, { params: { valid_to: validTo } }),
  deleteAssignment: (id: number) => api.delete(`/thlogger/assignments/${id}`),
  /** 주기 변경 예약 — 다음 업링크 응답(다운링크)으로 기기에 전달된다 */
  requestConfig: (id: number, data: { tdc_sec: number; n_entries: number; tr_min?: number }) => api.post(`/thlogger/devices/${id}/config`, data),
  cancelConfig: (id: number) => api.delete(`/thlogger/devices/${id}/config`),
  /** 설치 지점 — 기록의 주인. 기기는 기간을 두고 지점에 설치된다 */
  points: (includeInactive = false) => api.get('/thlogger/points', { params: { include_inactive: includeInactive } }),
  createPoint: (data: any) => api.post('/thlogger/points', data),
  updatePoint: (id: number, data: any) => api.put(`/thlogger/points/${id}`, data),
  deletePoint: (id: number) => api.delete(`/thlogger/points/${id}`),
  pointHistory: (id: number) => api.get(`/thlogger/points/${id}/history`),
  /** 현황판 배치 — 카드 순서 · 배경색 (모든 사용자 공용) */
  savePointLayout: (data: { ids: number[]; colors: Record<string, string>; bars?: Record<string, string>; sizes?: Record<string, string> }) => api.put('/thlogger/points/layout', data),
  swapPoint: (id: number, data: any) => api.post(`/thlogger/points/${id}/swap`, data),
  updatePointDevice: (id: number, data: any) => api.put(`/thlogger/point-devices/${id}`, data),
  deletePointDevice: (id: number) => api.delete(`/thlogger/point-devices/${id}`),
  createPointAssignment: (id: number, data: any) => api.post(`/thlogger/points/${id}/assignments`, data),
  readings: (params: { device_id?: number; point_id?: number; from?: string; to?: string; bucket?: string }) =>
    api.get('/thlogger/readings', { params }),
  exportExcel: (params: { device_id?: number; point_id?: number; from?: string; to?: string }) =>
    api.get('/thlogger/export/excel', { params, responseType: 'blob' }),
  uplinks: (params: { device_id?: number; page?: number; page_size?: number }) => api.get('/thlogger/uplinks', { params }),
  rejects: (params: { page?: number; page_size?: number }) => api.get('/thlogger/rejects', { params }),
  clearRejects: () => api.delete('/thlogger/rejects'),
  scheduleEnv: (scheduleId: number) => api.get(`/thlogger/schedules/${scheduleId}/env`),
};
